import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const SKIP_REASONS = [
  { value: 'weather_rain',      label: '🌧 Rain' },
  { value: 'weather_snow',      label: '❄️ Snow' },
  { value: 'weather_wind',      label: '💨 High winds' },
  { value: 'weather_cold',      label: '🥶 Too cold' },
  { value: 'weather_heat',      label: '🔥 Too hot' },
  { value: 'weather_lightning', label: '⚡ Lightning / storm' },
  { value: 'stall_rest',        label: '🩹 Stall rest' },
  { value: 'injury',            label: '🤕 Injury' },
  { value: 'illness',           label: '🤒 Illness' },
  { value: 'vet',               label: '🩺 Vet visit today' },
  { value: 'farrier',           label: '🔨 Farrier today' },
  { value: 'owner_request',     label: '👤 Owner request' },
  { value: 'footing',           label: '🚫 Poor footing' },
  { value: 'other',             label: '📝 Other' },
];

const EARLY_IN_REASONS = [
  { value: 'weather',       label: '🌧 Weather came in' },
  { value: 'heat',          label: '🥵 Too hot' },
  { value: 'flies',         label: '🐝 Flies / bugs' },
  { value: 'came_in_lame',  label: '🩹 Came in lame' },
  { value: 'behavior',      label: '😤 Behavior issue' },
  { value: 'owner_request', label: '👤 Owner request' },
  { value: 'vet_farrier',   label: '🩺 Vet / farrier coming' },
  { value: 'other',         label: '📝 Other' },
];

const sh = {
  primaryBtn: {
    minHeight: 64, width: '100%', background: 'var(--cr-primary)', color: '#fff',
    border: 'none', borderRadius: 12, fontSize: 18, fontWeight: 600,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, cursor: 'pointer', marginBottom: 12,
  },
  secondaryBtn: {
    minHeight: 56, width: '100%', background: '#fff', color: 'var(--cr-primary)',
    border: '2px solid var(--cr-primary)', borderRadius: 12, fontSize: 18, fontWeight: 600,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, cursor: 'pointer', marginBottom: 12,
  },
  dangerBtn: {
    minHeight: 56, width: '100%', background: 'var(--cr-red-light)', color: 'var(--cr-red)',
    border: '2px solid var(--cr-red)', borderRadius: 12, fontSize: 18, fontWeight: 600,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, cursor: 'pointer', marginBottom: 12,
  },
  card: { background: '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  label: { fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 },
};

const fmtTime = (d, tz) => {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz || undefined });
};

const fmtElapsed = (sinceDate) => {
  if (!sinceDate) return '';
  const diff = Date.now() - new Date(sinceDate).getTime();
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
};

// ── Reason picker sheet ───────────────────────────────────────────────────────
function ReasonSheet({ reasons, onSelect, onClose }) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999 }} onClick={onClose} />
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1000, background: '#fff', borderRadius: '20px 20px 0 0', padding: '16px 16px 40px' }}>
        <div style={{ width: 40, height: 4, background: '#ddd', borderRadius: 2, margin: '0 auto 16px' }} />
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Select a reason</div>
        {reasons.map(r => (
          <button key={r.value} onClick={() => onSelect(r.value)} style={{
            width: '100%', minHeight: 52, background: 'var(--bg-secondary)', border: 'none',
            borderRadius: 10, fontSize: 16, fontWeight: 500, textAlign: 'left',
            padding: '0 16px', marginBottom: 8, cursor: 'pointer',
          }}>{r.label}</button>
        ))}
        <button onClick={onClose} style={{ width: '100%', minHeight: 52, background: 'none', border: '1px solid var(--border-color)', borderRadius: 10, fontSize: 16, cursor: 'pointer', marginTop: 4 }}>Cancel</button>
      </div>
    </>
  );
}

// ── Horse card ────────────────────────────────────────────────────────────────
function HorseCard({ horse, selected, onSelect, tz }) {
  const isOut = horse.current_status === 'out';
  const elapsed = isOut && horse.active_session ? fmtElapsed(horse.active_session.turned_out_at) : '';

  return (
    <div onClick={() => onSelect(horse.id)} style={{
      ...sh.card,
      border: selected ? '3px solid var(--cr-primary)' : `3px solid ${isOut ? 'var(--cr-green)' : 'var(--border-color)'}`,
      background: selected ? 'var(--cr-primary-light, #f0e8f0)' : '#fff',
      cursor: 'pointer',
      position: 'relative',
    }}>
      {selected && (
        <div style={{ position: 'absolute', top: 10, right: 12, width: 28, height: 28, background: 'var(--cr-primary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 16 }}>✓</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{horse.name}</div>
          {isOut && horse.active_session && (
            <div style={{ fontSize: 13, color: 'var(--cr-green-dark)', marginTop: 2 }}>
              🌿 Out since {fmtTime(horse.active_session.turned_out_at, tz)} · {elapsed}
            </div>
          )}
          {!isOut && horse.total_turnout_today_minutes > 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              Total today: {horse.total_turnout_today_formatted}
            </div>
          )}
          {horse.sessions?.some(s => s.status === 'skipped') && (
            <div style={{ fontSize: 12, color: 'var(--cr-amber)', marginTop: 2 }}>
              ⚠️ Skipped today
            </div>
          )}
        </div>
        <div style={{
          fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 20,
          background: isOut ? 'var(--cr-green-light)' : 'var(--bg-tertiary)',
          color: isOut ? 'var(--cr-green-dark)' : 'var(--text-muted)',
        }}>{isOut ? '🌿 OUT' : '🏠 IN'}</div>
      </div>
    </div>
  );
}

// ── Main turnout section ──────────────────────────────────────────────────────
function TurnoutSection({ org }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState([]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [view, setView]           = useState('today'); // today | history
  const [history, setHistory]     = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showSkipSheet, setShowSkipSheet]   = useState(false);
  const [showEarlySheet, setShowEarlySheet] = useState(false);
  const [ticker, setTicker]       = useState(0);
  const tz = org.timezone || 'America/Chicago';
  const isManager = org.my_role === 'manager';

  // Live elapsed timer — updates every minute
  useEffect(() => {
    const id = setInterval(() => setTicker(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/organizations/${org.id}/turnout/today`);
      setData(data);
    } catch { setData({ horses: [], can_turnout: false }); }
    finally { setLoading(false); }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/organizations/${org.id}/turnout/history`, { params: { days: 30 } });
      setHistory(data || []);
    } catch {} finally { setHistoryLoading(false); }
  };

  useEffect(() => { if (view === 'history') loadHistory(); }, [view]);

  const toggleSelect = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const selectAll = (status) => {
    const ids = (data?.horses || []).filter(h => h.current_status === status).map(h => h.id);
    setSelected(ids);
  };

  const canTurnOut = selected.length > 0 && selected.every(id => data?.horses.find(h => h.id === id)?.current_status === 'in');
  const canBringIn = selected.length > 0 && selected.every(id => data?.horses.find(h => h.id === id)?.current_status === 'out');
  const mixedSelection = selected.length > 0 && !canTurnOut && !canBringIn;

  const doTurnOut = async () => {
    setSaving(true); setError('');
    try {
      await api.post(`/organizations/${org.id}/turnout/out`, { horse_ids: selected });
      setSelected([]);
      await load();
    } catch (e) { setError(e.response?.data?.message || 'Could not record turnout'); }
    finally { setSaving(false); }
  };

  const doBringIn = async (earlyReason = null) => {
    setSaving(true); setError('');
    try {
      await api.post(`/organizations/${org.id}/turnout/in`, { horse_ids: selected, early_in_reason: earlyReason });
      setSelected([]);
      await load();
    } catch (e) { setError(e.response?.data?.message || 'Could not record bring-in'); }
    finally { setSaving(false); }
  };

  const doSkip = async (reason) => {
    setSaving(true); setError('');
    try {
      await api.post(`/organizations/${org.id}/turnout/skip`, { horse_ids: selected, skip_reason: reason });
      setSelected([]);
      await load();
    } catch (e) { setError(e.response?.data?.message || 'Could not record skip'); }
    finally { setSaving(false); }
  };

  const horses = data?.horses || [];
  const horsesOut = horses.filter(h => h.current_status === 'out');
  const horsesIn  = horses.filter(h => h.current_status === 'in');
  const canTurnout = data?.can_turnout;

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading turnout…</div>;

  return (
    <div>
      {showSkipSheet && <ReasonSheet reasons={SKIP_REASONS} onSelect={r => { setShowSkipSheet(false); doSkip(r); }} onClose={() => setShowSkipSheet(false)} />}
      {showEarlySheet && <ReasonSheet reasons={EARLY_IN_REASONS} onSelect={r => { setShowEarlySheet(false); doBringIn(r); }} onClose={() => setShowEarlySheet(false)} />}

      {/* View toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={() => setView('today')} style={{ flex: 1, minHeight: 44, border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer', background: view === 'today' ? 'var(--cr-primary)' : 'var(--bg-tertiary)', color: view === 'today' ? '#fff' : 'var(--text-secondary)' }}>Today</button>
        <button onClick={() => setView('history')} style={{ flex: 1, minHeight: 44, border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer', background: view === 'history' ? 'var(--cr-primary)' : 'var(--bg-tertiary)', color: view === 'history' ? '#fff' : 'var(--text-secondary)' }}>History (30 days)</button>
      </div>

      {error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}

      {/* ── Today view ── */}
      {view === 'today' && (
        <div>
          {horses.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🐴</div>
              <div>No horses assigned to you yet.</div>
            </div>
          )}

          {/* Action buttons — only show when horses selected */}
          {selected.length > 0 && !mixedSelection && canTurnout && (
            <div style={{ marginBottom: 16 }}>
              {canTurnOut && (
                <>
                  <button style={sh.primaryBtn} onClick={doTurnOut} disabled={saving}>
                    {saving ? 'Recording…' : `🌿 Turn out ${selected.length} horse${selected.length > 1 ? 's' : ''}`}
                  </button>
                  <button style={sh.secondaryBtn} onClick={() => setShowSkipSheet(true)} disabled={saving}>
                    ⛔ Mark as not going out today
                  </button>
                </>
              )}
              {canBringIn && (
                <>
                  <button style={sh.primaryBtn} onClick={() => doBringIn()} disabled={saving}>
                    {saving ? 'Recording…' : `🏠 Bring in ${selected.length} horse${selected.length > 1 ? 's' : ''}`}
                  </button>
                  <button style={sh.secondaryBtn} onClick={() => setShowEarlySheet(true)} disabled={saving}>
                    ⚠️ Bring in early — add reason
                  </button>
                </>
              )}
              <button style={{ ...sh.secondaryBtn, fontSize: 14, minHeight: 44 }} onClick={() => setSelected([])}>
                Clear selection
              </button>
            </div>
          )}

          {mixedSelection && (
            <div style={{ background: 'var(--cr-amber-light)', color: 'var(--cr-amber)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>
              Select horses that are all IN or all OUT to take action.
            </div>
          )}

          {/* Horses out */}
          {horsesOut.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={sh.label}>🌿 Out now ({horsesOut.length})</div>
                {horsesOut.length > 1 && canTurnout && (
                  <button onClick={() => selectAll('out')} style={{ fontSize: 12, color: 'var(--cr-primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Select all</button>
                )}
              </div>
              {horsesOut.map(h => (
                <HorseCard key={h.id} horse={h} selected={selected.includes(h.id)} onSelect={canTurnout ? toggleSelect : () => {}} tz={tz} />
              ))}
            </div>
          )}

          {/* Horses in */}
          {horsesIn.length > 0 && (
            <div style={{ marginTop: horsesOut.length > 0 ? 16 : 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={sh.label}>🏠 In ({horsesIn.length})</div>
                {horsesIn.length > 1 && canTurnout && (
                  <button onClick={() => selectAll('in')} style={{ fontSize: 12, color: 'var(--cr-primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Select all</button>
                )}
              </div>
              {horsesIn.map(h => (
                <HorseCard key={h.id} horse={h} selected={selected.includes(h.id)} onSelect={canTurnout ? toggleSelect : () => {}} tz={tz} />
              ))}
            </div>
          )}

          {/* Today's session log */}
          {horses.some(h => h.sessions?.length > 0) && (
            <div style={{ marginTop: 24 }}>
              <div style={sh.label}>Today's turnout log</div>
              {horses.filter(h => h.sessions?.length > 0).map(h => (
                <div key={h.id} style={sh.card}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
                    {h.name}
                    {h.total_turnout_today_minutes > 0 && (
                      <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--text-secondary)', marginLeft: 8 }}>
                        Total: {h.total_turnout_today_formatted}
                      </span>
                    )}
                  </div>
                  {h.sessions.map((s, i) => (
                    <div key={s.id} style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '4px 0', borderTop: i > 0 ? '1px solid var(--border-color)' : 'none' }}>
                      {s.status === 'skipped' ? (
                        <span>⛔ Not turned out — {SKIP_REASONS.find(r => r.value === s.skip_reason)?.label || s.skip_reason}</span>
                      ) : (
                        <span>
                          {fmtTime(s.turned_out_at, tz)} → {s.brought_in_at ? fmtTime(s.brought_in_at, tz) : '⏳ Still out'}
                          {s.duration_formatted ? ` · ${s.duration_formatted}` : ''}
                          {s.early_in_reason ? ` · ${EARLY_IN_REASONS.find(r => r.value === s.early_in_reason)?.label || s.early_in_reason}` : ''}
                          {s.edit_note ? ' · ✏️ Edited' : ''}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── History view ── */}
      {view === 'history' && (
        <div>
          {historyLoading && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Loading…</div>}
          {!historyLoading && history.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No turnout history yet.</div>
          )}
          {/* Group by date */}
          {!historyLoading && (() => {
            const byDate = {};
            history.forEach(s => {
              if (!byDate[s.session_date]) byDate[s.session_date] = {};
              if (!byDate[s.session_date][s.horse_id]) byDate[s.session_date][s.horse_id] = { horse: s.horse, sessions: [] };
              byDate[s.session_date][s.horse_id].sessions.push(s);
            });
            return Object.keys(byDate).map(date => (
              <div key={date} style={sh.card}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
                  {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </div>
                {Object.values(byDate[date]).map(({ horse, sessions }) => {
                  const totalMins = sessions.filter(s => s.status === 'in').reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
                  const totalFmt = totalMins > 0 ? `${Math.floor(totalMins/60)}h ${(totalMins%60).toString().padStart(2,'0')}m` : null;
                  return (
                    <div key={horse?.id} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border-color)' }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {horse?.name}
                        {totalFmt && <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>Total: {totalFmt}</span>}
                      </div>
                      {sessions.map(s => (
                        <div key={s.id} style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {s.status === 'skipped'
                            ? `⛔ ${SKIP_REASONS.find(r => r.value === s.skip_reason)?.label || 'Skipped'}`
                            : `${fmtTime(s.turned_out_at, tz)} → ${s.brought_in_at ? fmtTime(s.brought_in_at, tz) : 'Still out'} ${s.duration_formatted ? '· ' + s.duration_formatted : ''}`
                          }
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ));
          })()}

          {isManager && (
            <div style={{ marginTop: 16, ...sh.card }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>📥 Export turnout data</div>
              <div style={sh.label}>Start date</div>
              <input type="date" id="exp-start" style={{ width: '100%', minHeight: 48, padding: '0 14px', fontSize: 16, borderRadius: 10, border: '1.5px solid var(--border-color)', boxSizing: 'border-box', marginBottom: 10 }} />
              <div style={sh.label}>End date</div>
              <input type="date" id="exp-end" style={{ width: '100%', minHeight: 48, padding: '0 14px', fontSize: 16, borderRadius: 10, border: '1.5px solid var(--border-color)', boxSizing: 'border-box', marginBottom: 10 }} />
              <button style={sh.primaryBtn} onClick={async () => {
                const start = document.getElementById('exp-start').value;
                const end = document.getElementById('exp-end').value;
                if (!start || !end) return;
                const res = await api.get(`/organizations/${org.id}/turnout/export`, { params: { start, end }, responseType: 'blob' });
                const url = URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = `${org.name}-turnout-${start}-to-${end}.csv`; a.click();
                URL.revokeObjectURL(url);
              }}>⬇️ Download CSV</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TurnoutSection;
